import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Globe, ExternalLink, Search, Plus, BookOpen, X, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const FCI_BADGE_VARIANT = {
  Yes: 'default',
  No: 'secondary',
  Independent: 'outline',
  Expelled: 'destructive',
};

export default function KennelClubs() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    country: '', name: '', website: '', fci_status: '', notes: '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/kennel-clubs`);
        if (cancelled) return;
        setItems(res.data.items || []);
        setSource(res.data.source);
      } catch {
        toast.error('Could not load kennel clubs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? items.filter((i) =>
          i.country.toLowerCase().includes(q) || i.name.toLowerCase().includes(q),
        )
      : items;
    const map = new Map();
    for (const it of filtered) {
      if (!map.has(it.country)) map.set(it.country, []);
      map.get(it.country).push(it);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [items, search]);

  const handleSubmitSuggestion = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Please sign in to suggest a kennel club');
      navigate('/login', { state: { from: '/kennel-clubs' } });
      return;
    }
    if (!form.country.trim() || !form.name.trim()) {
      toast.error('Country and club name are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/kennel-clubs/suggest`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Thanks — your suggestion is queued for review');
      setSuggestOpen(false);
      setForm({ country: '', name: '', website: '', fci_status: '', notes: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit suggestion');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl" data-testid="kennel-clubs-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="text-4xl font-bold tracking-tight flex items-center gap-2">
            <Globe className="w-8 h-8 text-amber-600" />
            Kennel Clubs Directory
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            National and regional kennel clubs around the world, with FCI membership status
            and links to their official sites where available.
          </p>
        </div>
        <Button
          onClick={() => setSuggestOpen(true)}
          data-testid="suggest-kennel-club-btn"
          className="shrink-0"
        >
          <Plus className="w-4 h-4 mr-2" /> Suggest Another
        </Button>
      </div>

      <div className="relative max-w-md mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by country or club name…"
          className="pl-9"
          data-testid="kennel-clubs-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : grouped.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No kennel clubs match your search.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8" data-testid="kennel-clubs-list">
          {grouped.map(([country, clubs]) => (
            <section key={country} data-testid={`kennel-clubs-group-${country}`}>
              <h2 className="text-lg font-semibold tracking-tight mb-2 pb-1 border-b border-border">
                {country}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {clubs.map((c) => (
                  <Card key={c.slug} className="hover:shadow-md transition-shadow">
                    <CardContent className="py-4 px-5 flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium leading-tight">{c.name}</div>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {c.fci_status && (
                            <Badge
                              variant={FCI_BADGE_VARIANT[c.fci_status] || 'outline'}
                              className="text-xs"
                            >
                              FCI: {c.fci_status}
                            </Badge>
                          )}
                          {c.wikipedia_url && (
                            <a
                              href={c.wikipedia_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                            >
                              <BookOpen className="w-3 h-3" /> Wikipedia
                            </a>
                          )}
                        </div>
                      </div>
                      {c.website ? (
                        <a
                          href={c.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-amber-700 hover:text-amber-900 font-medium shrink-0"
                          data-testid={`kennel-club-link-${c.slug}`}
                        >
                          Visit <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground italic shrink-0">
                          no website
                        </span>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {source && (
        <p className="text-xs text-muted-foreground mt-10 pt-4 border-t border-border">
          Data retrieved from{' '}
          <a
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-foreground"
          >
            {source.name}
          </a>{' '}
          on {source.retrieved_date}. Content licensed under {source.license}.
          Spot something out of date? Use “Suggest Another” above and we'll review it.
        </p>
      )}

      {/* Suggest dialog */}
      <Dialog open={suggestOpen} onOpenChange={setSuggestOpen}>
        <DialogContent data-testid="suggest-dialog">
          <DialogHeader>
            <DialogTitle>Suggest a kennel club</DialogTitle>
            <DialogDescription>
              Know a kennel club we're missing — or have an updated link for an existing one?
              Send it our way and an admin will review it.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitSuggestion} className="space-y-3">
            <div>
              <Label htmlFor="kc-country">Country *</Label>
              <Input
                id="kc-country"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                placeholder="e.g. Iceland"
                data-testid="kc-suggest-country"
                required
              />
            </div>
            <div>
              <Label htmlFor="kc-name">Club name *</Label>
              <Input
                id="kc-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Official club name"
                data-testid="kc-suggest-name"
                required
              />
            </div>
            <div>
              <Label htmlFor="kc-website">Website</Label>
              <Input
                id="kc-website"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="https://…"
                data-testid="kc-suggest-website"
              />
            </div>
            <div>
              <Label htmlFor="kc-fci">FCI status</Label>
              <Input
                id="kc-fci"
                value={form.fci_status}
                onChange={(e) => setForm({ ...form, fci_status: e.target.value })}
                placeholder="Yes / No / Independent / unknown"
                data-testid="kc-suggest-fci"
              />
            </div>
            <div>
              <Label htmlFor="kc-notes">Notes</Label>
              <Textarea
                id="kc-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Anything else that helps us verify…"
                rows={3}
                data-testid="kc-suggest-notes"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setSuggestOpen(false)}
                disabled={submitting}
              >
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="kc-suggest-submit">
                {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                Submit
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
